/**
 * 지도 좌우의 시·도별 현황 (DESIGN §8.6.9).
 *
 * 지도를 세로 비율로 바꾸면서 중앙 컬럼 좌우에 공간이 남았다. 그 자리에
 * 16개 시도를 소형 다중(small multiples)으로 세운다.
 *
 * **막대는 그 시·도의 색 분포다.** 지도에서 경기가 여러 색으로 칠해져 있듯
 * 경기 막대도 그 색들이 얼마씩인지를 쌓아 보여준다 — 실측 경기 1,574칸이
 * 5구간에 266/312/366/338/292 로 퍼진다. 값 하나로 접으면 이 사실이 사라진다.
 *
 * 구간·색은 **지도 범례와 같은 객체**를 페이지에서 받아 쓴다. 따로 계산하면
 * 같은 화면에서 두 색 규칙이 생겨 서로를 반박한다.
 *
 * H3 칸은 행정경계와 정렬되지 않아 칸의 시·도는 **최빈값**이다 (실측 r7 96.9%
 * 가 단일 시도, 다수결 오차 0.89%). 혼재 칸 수를 각주에 적는다.
 */

import { useMemo } from 'react'

import type { Bins } from '@/lib/bins'
import { int, pct } from '@/lib/fmt'
import { useFilters } from '@/stores/filters'
import type { Cell } from './useGeoData'
import { useSidoRows } from './useSidoRows'
import './SidoFlank.css'

/** 좌우로 나눌 때 한 쪽에 몇 개까지 둘지 — 16개를 8+8 로 가른다 */
const PER_SIDE = 8

/** 한 시도가 갖는 막대 셋. 순서는 "얼마나 봤나 → 얼마나 잘 도나 → 얼마나 많나" */
const METRICS = [
  { key: 'coverage', label: '커버', fmt: (v: number) => pct(v, 0) },
  { key: 'availability', label: '가동', fmt: (v: number) => pct(v, 1) },
  { key: 'n_chargers', label: '대수', fmt: (v: number) => int(v) },
] as const

type MetricKey = (typeof METRICS)[number]['key']

interface Slot {
  counts: number[]
  na: number
  n: number
}

interface Props {
  side: 'left' | 'right'
  /** 지도 범례와 **같은 객체**. 여기서 다시 계산하지 않는다 */
  binsBy: Record<MetricKey, Bins>
  /** 지도에 그려진 칸 — 시·도별로 나눠 색 분포를 만든다 */
  cells: Cell[]
  /** 두 시·도 이상이 섞인 칸 수 (최빈 배정의 오차) */
  nMixed?: number
}

export function SidoFlank({ side, binsBy, cells, nMixed = 0 }: Props) {
  const filters = useFilters()
  const { rows, label, isLoading } = useSidoRows()
  const picked = filters.zcode

  /**
   * 시·도 × 지표 → 구간별 칸 수. 이것이 막대의 실체다.
   * 값이 없는 칸은 따로 세어 해칭으로 그린다 — 0 과 같아 보이면 안 된다.
   */
  const dist = useMemo(() => {
    const out = new Map<string, Record<MetricKey, Slot>>()
    for (const c of cells) {
      if (!c.zcode) continue
      let e = out.get(c.zcode)
      if (!e) {
        e = {} as Record<MetricKey, Slot>
        for (const m of METRICS) {
          e[m.key] = {
            counts: new Array<number>(binsBy[m.key].colors.length).fill(0),
            na: 0,
            n: 0,
          }
        }
        out.set(c.zcode, e)
      }
      for (const m of METRICS) {
        const v = c[m.key] as number | null
        const i = binsBy[m.key].indexOf(v)
        const slot = e[m.key]
        slot.n += 1
        if (i == null) slot.na += 1
        else slot.counts[i] = (slot.counts[i] ?? 0) + 1
      }
    }
    return out
  }, [cells, binsBy])

  /** 모든 칸이 한 구간이라 색이 하나뿐인 지표 — 분포가 없다는 사실을 적는다 */
  const collapsed = useMemo(() => {
    const out = {} as Record<MetricKey, boolean>
    for (const m of METRICS) {
      const used = new Set<number>()
      for (const e of dist.values()) {
        e[m.key].counts.forEach((c, i) => {
          if (c > 0) used.add(i)
        })
      }
      out[m.key] = used.size <= 1 && binsBy[m.key].colors.length > 1
    }
    return out
  }, [dist, binsBy])

  if (isLoading) return <div className="sflank" aria-hidden="true" />

  const slice = side === 'left' ? rows.slice(0, PER_SIDE) : rows.slice(PER_SIDE)

  return (
    <div className="sflank">
      {side === 'left' && (
        <div className="sflank__cap">
          <p className="sflank__captitle">시·도별 현황</p>
          <p className="sflank__caphint">
            막대 = 그 시·도 칸들의 색 분포 · 오른쪽 = 시·도 전체 값
          </p>
        </div>
      )}

      <ul className="sflank__list">
        {slice.map((r) => {
          const on = picked.includes(r.entity_id)
          const name = (label.get(r.entity_id) ?? r.entity_id).replace(
            /(특별자치도|특별자치시|광역시|특별시|통합특별시|도)$/,
            '',
          )
          const nCells = dist.get(r.entity_id)?.coverage.n ?? 0
          return (
            <li key={r.entity_id}>
              <button
                type="button"
                className={`sflank__row${on ? ' is-on' : ''}${r.held ? ' is-held' : ''}`}
                aria-pressed={on}
                onClick={() => filters.toggleList('zcode', r.entity_id)}
              >
                <span className="sflank__name">
                  {name}
                  {r.held && (
                    <span className="sflank__held" title="관측 커버리지 미달 — 순위에서 분리">
                      보류
                    </span>
                  )}
                  <span className="sflank__ncell num">{int(nCells)}칸</span>
                </span>

                {METRICS.map((m) => {
                  const bins = binsBy[m.key]
                  const d = dist.get(r.entity_id)?.[m.key]
                  const v = r[m.key] as number | null
                  const n = d?.n ?? 0
                  return (
                    <span className="sflank__metric" key={m.key}>
                      <span className="sflank__mlabel">{m.label}</span>
                      {/* 100% 누적 — 이 시·도의 칸들이 어느 색에 얼마나 있는가 */}
                      <span className="sflank__track">
                        {n === 0 ? (
                          <span className="sflank__seg hatch-na" style={{ width: '100%' }} />
                        ) : (
                          <>
                            {(d?.counts ?? []).map((cnt, i) =>
                              cnt === 0 ? null : (
                                <span
                                  key={bins.colors[i] ?? i}
                                  className="sflank__seg"
                                  style={{
                                    width: `${(cnt / n) * 100}%`,
                                    background: bins.colors[i],
                                  }}
                                  title={`${i + 1}번째 구간 — ${int(cnt)}칸 (${pct(cnt / n, 0)})`}
                                />
                              ),
                            )}
                            {(d?.na ?? 0) > 0 && (
                              <span
                                className="sflank__seg hatch-na"
                                style={{ width: `${((d?.na ?? 0) / n) * 100}%` }}
                                title={`값 없음 ${int(d?.na ?? 0)}칸`}
                              />
                            )}
                          </>
                        )}
                      </span>
                      <span className="sflank__mval num">{v == null ? '—' : m.fmt(v)}</span>
                    </span>
                  )
                })}
              </button>
            </li>
          )
        })}
      </ul>

      {side === 'right' && (
        <div className="sflank__foot">
          <ul className="sflank__legend">
            {METRICS.map((m) => {
              const b = binsBy[m.key]
              return (
                <li key={m.key} className="sflank__legrow">
                  <span className="sflank__mlabel">
                    {m.label}
                    {collapsed[m.key] && (
                      <span
                        className="sflank__flat"
                        title="모든 칸이 같은 구간이라 색이 하나입니다 — 이 지표는 분포가 없습니다"
                      >
                        !
                      </span>
                    )}
                  </span>
                  <span className="sflank__swatches">
                    {b.colors.map((c, i) => (
                      <span key={c} className="sflank__swcell">
                        <span className="sflank__sw" style={{ background: c }} />
                        {/* 경계를 인쇄한다 — 숨긴 분위 색은 거짓말이다 */}
                        <span className="sflank__edge">
                          {i < b.edges.length ? m.fmt(b.edges[i] as number) : ''}
                        </span>
                      </span>
                    ))}
                  </span>
                </li>
              )
            })}
          </ul>
          <p className="sflank__caveat">
            누르면 그 시·도만 봅니다. 막대는{' '}
            <strong>지도에서 그 시·도가 어떤 색으로 칠해져 있는지</strong>의 구성비이며
            지도 범례와 같은 구간을 씁니다.
            {nMixed > 0 && (
              <>
                {' '}두 시·도가 섞인 칸 {int(nMixed)}개는 <strong>최빈 시·도</strong>로
                셌습니다.
              </>
            )}
          </p>
          {METRICS.some((m) => collapsed[m.key]) && (
            <p className="sflank__caveat">
              <strong>!</strong> 표시된 지표는 모든 칸이 같은 구간이라 분포가 한 색뿐입니다.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
